import {
  DEFAULT_ALBRECHT_TARGETS,
  TRI_STATE,
  type RecInputs,
} from "./fertility";
import {
  replacementForSeasons,
  resolveSeasons,
  DEFAULT_SEASON_CROPS,
  type Replacement,
  type SeasonPlan,
  type YieldMaps,
} from "./replacement";

// The editor's per-cell inputs. The store keeps the session; this file turns
// tunables into the soil arrays the recommendation engine reads. Assembly
// stays in the browser.

export type { SeasonPlan };

export interface RxTunables {
  phTarget: number; // buildup basis — the pH to lime to
  caPctTarget: number;
  // The ECCE and the two product-efficiency knobs were removed rather than pinned at 100, so there
  // is no second place the same conversion can be applied and no way to end up dividing twice.
  minTAc: number; // rate floor (0 = none); min floors every non-dropped cell, and the window applies to the final rate
  dropBelowTAc: number | null; // empty = off. Cells whose final lime rate is below this get 0
  // Cap is on tons of 90% CCE lime, the same basis as the pH + CEC rate.
  maxTAc: number; // rate ceiling — the per-application spreading limit, editable per field
  // Three preset shapes share one tunable set; the panel shows the subset each basis actually uses
  // so a knob is never visible where it does nothing.
  kPctTarget: number; // Albrecht K: target K base saturation (% of CEC)
  kCritHighCec: number; // build+maintain: critical ppm where CEC > 5
  kCritLowCec: number; // build+maintain: critical ppm where CEC ≤ 5
  kMinLbAc: number;
  kMaxLbAc: number; // 0 = no ceiling (unlike lime, K has no platform-wide spreading limit)
  kDropBelowLbAc: number | null; // empty = off. Cells whose final K₂O rate is below this get 0
  // Per-season rather than panel-wide because coverage differs by crop: a field can have real
  // machine yield for its soybean years and only simulated for corn, and forcing one basis across
  // the rotation meant taking the worse data for half of it.
  seasons: SeasonPlan[];
  flatRateLbAc: number; // the SEED value a newly-flat season starts at; each season then owns its own
  removalPct: number; // share of crop removal to replace (80 = "put back 80% of what it takes")
  pCrit: number; // ppm critical the buildup targets
  pMinLbAc: number;
  pMaxLbAc: number; // 0 = no ceiling
  pDropBelowLbAc: number | null; // empty = off. Cells whose final P₂O₅ rate is below this get 0
  pMinPct: number; // soil-health (Haney) credit multiplier, PERCENT — 0 ignores the biology credit
  /*
   *
   * Typing a budget solves for the critical and writes it; typing a critical clears the budget
   * because the number would otherwise go stale and quietly misdescribe the rec.
   */
  budgetLbAc: number | null;
  // PERCENTAGES on a 0–150 slider: 100 = the adjustment exactly as the preset computes it, 0 =
  // switched off, 150 = half again.
  diffusionPct: number; // shared by P and K — both use the moisture/diffusion adjustment
  kFixPct: number; // K only (clay fixation)
}

/* * Defaults are the engine's documented values, so opening the editor and saving changes nothing. */
export const DEFAULT_TUNABLES: RxTunables = {
  phTarget: TRI_STATE.PH_TARGET,
  caPctTarget: DEFAULT_ALBRECHT_TARGETS.caPct, // 65 — the platform's Albrecht profile
  minTAc: 0,
  maxTAc: TRI_STATE.LIME_MAX_T_AC,
  dropBelowTAc: null,
  kPctTarget: DEFAULT_ALBRECHT_TARGETS.kPct,
  kCritHighCec: TRI_STATE.K_CRIT_PPM_HIGH_CEC,
  kCritLowCec: TRI_STATE.K_CRIT_PPM_LOW_CEC,
  kMinLbAc: 0,
  kMaxLbAc: 0,
  kDropBelowLbAc: null,
  pCrit: TRI_STATE.P_CRIT_PPM,
  pMinLbAc: 0,
  pMaxLbAc: 0,
  pDropBelowLbAc: null,
  pMinPct: 100,
  budgetLbAc: null,
  // The preset rotation: TWO seasons, corn then soybean. The crop list is in the
  // bundle, so this snapshot is taken after those rows exist.
  seasons: resolveSeasons(DEFAULT_SEASON_CROPS, { machine: {}, simulated: {} }),
  flatRateLbAc: 60,
  removalPct: 100,
  diffusionPct: 100, // = exactly what the preset does today, so opening the editor changes nothing
  kFixPct: 100,
};

export interface Base {
  soil: RecInputs;
  geoms: { type: string; coordinates: unknown }[];
  /** `/grid`.boundary_version_id at the moment the session opened. */
  gridId: string;
  cellOrder: "grid";
  cellCount: number;
  sources: Record<string, string>;
  // ONE EXPENSIVE STEP, ONCE (the store's founding rule): both variable-rate yield bases are fetched at
  // `begin`, so switching machine⇄simulated or adding a season is arithmetic on arrays already in memory.
  // Fetching on change would put a network round-trip behind a radio button.
  yieldMaps: YieldMaps;
}

function slider(pct: number): number {
  return (Number.isFinite(pct) ? pct : 100) / 100;
}

// `lab + m·(diffused − lab)`. m=1 keeps the adjusted map; m=0 is the lab value.
// Scaling the adjusted map itself would make m=0 mean the nutrient is absent.
function scaleDeparture(
  adjusted: (number | null)[],
  lab: (number | null)[] | undefined,
  m: number,
): (number | null)[] {
  if (!lab || m === 1) return adjusted;
  return adjusted.map((v, i) => {
    const base = lab[i];
    return v == null || base == null ? v : base + m * (v - base);
  });
}

function copyDrop(
  out: RecInputs,
  key: "pDropBelowLbAc" | "kDropBelowLbAc" | "limeDropBelowTAc",
  value: number | null | undefined,
): void {
  if (value != null && Number.isFinite(value) && value >= 0) out[key] = value;
  else delete out[key];
}

function applyLime(
  out: RecInputs,
  soil: RecInputs,
  t: RxTunables,
  albrecht: boolean,
): RecInputs {
  copyDrop(out, "limeDropBelowTAc", t.dropBelowTAc);
  if (albrecht) {
    out.albrechtTargets = {
      ...(soil.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS),
      caPct: t.caPctTarget,
    };
  } else {
    out.phTargetCustom = t.phTarget;
  }
  return out;
}

function applyP(out: RecInputs, soil: RecInputs, t: RxTunables, base?: Base) {
  out.pCritCustom = t.pCrit;
  out.pMinLbAc = t.pMinLbAc;
  out.pMaxLbAc = t.pMaxLbAc;
  copyDrop(out, "pDropBelowLbAc", t.pDropBelowLbAc);
  out.p = scaleDeparture(soil.p, soil.pRaw, slider(t.diffusionPct));
  // The credit's baseline is zero, so the slider scales the credit itself:
  // 0 ignores it, 100 keeps it, 150 trusts it half again.
  const cm = slider(t.pMinPct);
  if (soil.pMinCredit && cm !== 1) {
    out.pMinCredit = soil.pMinCredit.map((c) =>
      c == null ? c : Math.max(0, c * cm),
    );
  }
  if (!base) return;
  const repl = replacementForSeasons(
    t.seasons,
    base.yieldMaps,
    base.cellCount,
    {
      removalPct: t.removalPct,
      flatFallback: t.flatRateLbAc,
      nutrient: "p2o5",
    },
  );
  if (repl) out.pMaintenance = repl.p2o5;
}

function applyK(
  out: RecInputs,
  soil: RecInputs,
  t: RxTunables,
  albrecht: boolean,
  base?: Base,
) {
  out.kMinLbAc = t.kMinLbAc;
  out.kMaxLbAc = t.kMaxLbAc;
  copyDrop(out, "kDropBelowLbAc", t.kDropBelowLbAc);
  if (albrecht) {
    out.albrechtTargets = {
      ...(soil.albrechtTargets ?? DEFAULT_ALBRECHT_TARGETS),
      kPct: t.kPctTarget,
    };
  } else {
    out.kCritHighCec = t.kCritHighCec;
    out.kCritLowCec = t.kCritLowCec;
  }
  out.k = scaleDeparture(soil.k, soil.kRaw, slider(t.diffusionPct));
  // Efficiency is a departure from 1. Floored above 0 because the engine divides by it.
  const fixM = slider(t.kFixPct);
  if (soil.kFixEff && fixM !== 1) {
    out.kFixEff = soil.kFixEff.map((e) =>
      e == null ? e : Math.max(0.05, 1 + fixM * (e - 1)),
    );
  }
  if (!base) return;
  const repl = replacementFor(t, base);
  if (repl) out.kMaintenance = repl.k2o;
}

/* * Apply the panel's tunables onto the base soil. Pure — the recompute path depends on nothing else. */
export function seededBounds(
  nutrient: "lime" | "k2o" | "p2o5",
  bounds?: { min: number; max: number; dropBelow?: number } | null,
): Partial<RxTunables> {
  if (!bounds) return {};
  if (nutrient === "p2o5") {
    return {
      pMinLbAc: bounds.min,
      pMaxLbAc: bounds.max,
      pDropBelowLbAc: bounds.dropBelow ?? null,
    };
  }
  if (nutrient === "k2o") {
    return {
      kMinLbAc: bounds.min,
      kMaxLbAc: bounds.max,
      kDropBelowLbAc: bounds.dropBelow ?? null,
    };
  }
  return { dropBelowTAc: bounds.dropBelow ?? null };
}

export function withTunables(
  soil: RecInputs,
  t: RxTunables,
  albrecht: boolean,
  nutrient: "lime" | "k2o" | "p2o5" = "lime",
  base?: Base,
): RecInputs {
  const out: RecInputs = {
    ...soil,
    limeMinTAc: t.minTAc,
    limeMaxTAc: t.maxTAc,
    rateWindow: "final",
  };
  if (nutrient === "lime") return applyLime(out, soil, t, albrecht);
  if (nutrient === "p2o5") {
    applyP(out, soil, t, base);
    return out;
  }
  applyK(out, soil, t, albrecht, base);
  return out;
}

/*
 *
 * The maintenance half: seasons × crops → a `Replacement`, from whichever basis the panel is set to.
 * Pure and synchronous — the yield maps were fetched once at `begin`.
 */
export function replacementFor(t: RxTunables, base: Base): Replacement | null {
  // delegates to the SHARED builder the preset run and the preview card use — one path, so an edited Rx
  // and the card it was opened from can never compute maintenance differently
  return replacementForSeasons(t.seasons, base.yieldMaps, base.cellCount, {
    removalPct: t.removalPct,
    flatFallback: t.flatRateLbAc,
  });
}

/*
 *
 * The assemble options for a session — one place, so `set` and `save` can never build different
 * surfaces. K deliberately passes `replacement: null`.
 */
export function optsFor(
  nutrient: "lime" | "k2o" | "p2o5",
  _t: RxTunables,
  _base: Base,
) {
  if (nutrient === "lime")
    return {
      nutrients: ["lime"],
      cation: false,
      replacement: null as Replacement | null,
      rateWindow: "final" as const,
    };
  // P: maintenance rides `soil.pMaintenance`, so no separate (unclamped) total layer
  if (nutrient === "p2o5")
    return {
      nutrients: ["p2o5"],
      cation: false,
      replacement: null as Replacement | null,
      rateWindow: "final" as const,
    };
  // `cation: false` — the Genius K already carries cation balance inside the diffusion supply (Schofield AR
  // uses Ca/Mg), and the preset path suppresses the BCSR step for exactly that reason. Re-adding it here
  // would double-count it on the one preset that needs it least.
  return {
    nutrients: ["k2o"],
    cation: false,
    replacement: null as Replacement | null,
    rateWindow: "final" as const,
  };
}
